import { Measure } from '@cbsi-cbscom/measure';
import { AssetTypePM } from 'library/ts/model/enums/metrics/performance/AssetTypePM';
import { UserTypePM } from 'library/ts/model/enums/metrics/performance/UserTypePM';
import { UserType } from 'library/ts/model/user/UserType';
import { PlayerAdapterEvent } from 'library/ts/player/adapter/events/PlayerAdapterEvent';
import { LogService } from 'library/ts/utilities/logger/LogService';
import { Metrics } from 'library/ts/utilities/measure/Metrics';
import { TagEvent } from 'tag/ts/tag/events/TagEvent';
import { NewRelicManager } from 'tag/ts/tag/metrics/NewRelicManager';
import { TagLoggerString } from 'tag/ts/tag/model/enums/TagLoggerString';
import { EmbedType } from 'tag/ts/tag/model/type/embed/EmbedType';
export class PerformanceMetricsManager {
    static _instance = new PerformanceMetricsManager();
    #logger;
    #newRelic;
    #tag;
    constructor() {
        this.#logger = LogService.create({ prefixes: ['SmartTag:', this.name + ':'] });
        this.#newRelic = NewRelicManager.getInstance();
    }
    get name() {
        return 'PerformanceMetricsManager';
    }
    static getInstance() {
        return PerformanceMetricsManager._instance;
    }
    get utils() {
        return window?.PARA?.PlayerServices?.Utilities;
    }
    #isHTML5() {
        return this.#tag?.params?.SYSTEM?.embedType === EmbedType.HTML5;
    }
    #embedType() {
        if (this.#isHTML5() === true) {
            return EmbedType.HTML5;
        }
        else {
            return EmbedType.DESKTOP;
        }
    }
    #isLive(contentParams) {
        if (this.utils.Type.isString(contentParams?.assetURL)
            && this.utils.Type.isEmpty(contentParams?.assetURL) === false
            && contentParams?.isLive === true) {
            return true;
        }
        else {
            return contentParams?.isLive;
        }
    }
    #isSyncbak(contentParams) {
        if (this.#isLive(contentParams) && this.utils.Type.isString(contentParams?.assetURL)) {
            return (this.utils.URL.parser(contentParams?.assetURL).host.indexOf('.syncbak.com') > -1);
        }
        return false;
    }
    #userType() {
        let output = UserTypePM.ANON;
        const userTypeString = this.#tag?.tagUtilities.getUserType(this.#tag?.params?.USER?.userStatus);
        switch (userTypeString) {
            case UserType.CF:
                output = UserTypePM.CF;
                break;
            case UserType.LC:
            case UserType.LCP:
                output = UserTypePM.LC;
                break;
            case UserType.ANON:
                output = UserTypePM.ANON;
                break;
            case UserType.MVPD_AUTHZ:
                output = UserTypePM.MVPD_AUTHZ;
                break;
        }
        return output;
    }
    #assetType() {
        if (this.#isLive(this.#tag?.params?.CONTENT) === false) {
            return AssetTypePM.VOD;
        }
        else if (this.#isSyncbak(this.#tag?.params?.CONTENT) === true) {
            return AssetTypePM.SYNCBAK;
        }
        else {
            return AssetTypePM.LIVE;
        }
    }
    #isSpa() {
        if (!this.utils.Type.isNullOrUndefined(this.#tag?.params?.CONTEXT?.metadata?.isSPA)) {
            return this.#tag?.params?.CONTEXT?.metadata?.isSPA;
        }
        else {
            return false;
        }
    }
    #isAutoplay() {
        if (this.#tag?.params?.CONTENT?.autoplay === false) {
            return false;
        }
        else if (this.#isHTML5() === false) {
            if (this.utils.Type.isFunction(this.#tag?.view?.skin?.API.SKIN_IN?.getSettingsVO)) {
                return this.#tag?.view?.skin?.API.SKIN_IN?.getSettingsVO()?.mute
                    ? window?.SmartTag.autoplay.supportsMutedAutoplay
                    : window?.SmartTag.autoplay.supportsUnmutedAutoplay;
            }
            else {
                return false;
            }
        }
        else {
            return true;
        }
    }
    #getUserId = () => {
        return this.#tag?.params?.USER?.userId;
    };
    setTag(tag) {
        this.#tag = tag;
        this.#initializeEventHandler();
        this.#newRelic.attributes = {
            'assetType': this.#assetType(),
            'embedType': this.#embedType(),
            'userId': this.#getUserId(),
            'userType': this.#userType()
        };
    }
    #setNewRelicPerformanceMetric = (metric, time) => {
        switch (metric) {
            case Metrics.importsLoadTime:
                if (this.#isHTML5() === false) {
                    this.#newRelic.addPageAction(metric, time);
                }
                break;
            case Metrics.importToCreateTime:
                if (this.#isHTML5() === false && this.#isSpa() === false) {
                    this.#newRelic?.addPageAction(metric, time);
                }
                break;
            default:
                this.#newRelic?.addPageAction(metric, time);
        }
    };
    #onPlayerEvent = (event, msFromStart) => {
        const onPlaybackStarted = () => {
            Measure.end(Metrics.attachToVideoStartTime);
            const report = Measure?.report({ roundValues: true });
            this.#logger.log('Measurements finished', report);
            if (this.#isAutoplay() === true) {
                const averages = report?.averages;
                for (const key in averages) {
                    if (!this.utils.Type.isNullOrUndefined(averages[key])) {
                        const time = +(averages[key] / 1000).toFixed(2);
                        this.#setNewRelicPerformanceMetric(key, time);
                    }
                }
            }
        };
        switch (event?.type) {
            case PlayerAdapterEvent.PLAYBACK_STARTED:
                this.#logger.log(TagLoggerString.ON_PLAYER_EVENT, event?.type, 'player adapter event:', event?.type, 'msFromStart:', msFromStart);
                onPlaybackStarted();
                break;
        }
    };
    #onTagEvent = (event) => {
        switch (event?.type) {
            case TagEvent.ON_READY:
                this.#newRelic.addAttributes();
                this.#newRelic.addRelease();
                this.#newRelic.setApplicationVersion();
                this.#tag?.eventDispatcher?.removeEventListener(event?.type, this.#onTagEvent);
                break;
            case TagEvent.ON_PLAYER_EVENT:
                this.#onPlayerEvent(event?.data?.playerAdapterEvent, event?.msFromStart);
                break;
        }
    };
    #initializeEventHandler() {
        if (!this.utils.Type.isNullOrUndefined(this.#tag)) {
            this.#tag?.tagEventsHandler(this.#onTagEvent, [
                TagEvent.ON_READY,
                TagEvent.ON_PLAYER_EVENT
            ], this);
        }
    }
}
